import { AuthService, ConfigStateService, CoreModule, LoginParams, ShowPasswordDirective } from '@abp/ng.core';
import { NG_BOOTSTRAP_CONFIG_PROVIDERS, ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RecaptchaService, eAccountComponents, SecurityCodeService, RecoveryCodeService, RECAPTCHA_STRATEGY, getRedirectUrl, REQUIRES_TWO_FACTOR, PERIODICALLY_CHANGE_PASSWORD, SHOULD_CHANGE_PASSWORD_ON_NEXT_LOGIN, AccountPublicModule } from '@volo/abp.ng.account/public';
import { IdentityLinkUserService, LinkUserInput } from '@volo/abp.ng.account/public/proxy';
import { from, of, pipe, throwError } from 'rxjs';
import { catchError, delay, finalize, switchMap, tap } from 'rxjs/operators';
import {
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { BiometricLoginService } from './services/biometric-login.service';
import { CustomToasterService } from '../toaster/toaster.service';
import { ConfigMenuService } from '../config-menu/config-menu.service';
import { StateService } from '../state-service/state.service';

const { maxLength, required } = Validators;

@Component({
  selector: 'abp-login',
  templateUrl: './login.component.html',
  standalone: true,
  styleUrl: "./login.component.scss",
  imports: [
    AccountPublicModule,
    ThemeSharedModule,
    CoreModule,
    ShowPasswordDirective,
    NgbTooltipModule,
    RouterLink
  ],
  providers: [RecaptchaService],
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('recaptcha', { static: false })
  recaptchaRef: ElementRef<HTMLDivElement>;

  form: UntypedFormGroup;

  inProgress: boolean;

  isSelfRegistrationEnabled = true;

  authWrapperKey = eAccountComponents.AuthWrapper;

  linkUser: LinkUserInput;

  showPassword = false;
  capsLock = false;

  isBiometricAvailable = false;
  useBiometricLogin = false;

  protected fb: UntypedFormBuilder;
  protected toasterService: CustomToasterService;
  protected authService: AuthService;
  protected configState: ConfigStateService;
  protected route: ActivatedRoute;
  protected router: Router;
  protected identityLinkUserService: IdentityLinkUserService;
  protected recaptchaService: RecaptchaService;
  protected securityCodeService: SecurityCodeService;
  protected readonly recoveryCodeService = inject(RecoveryCodeService);
  protected biometricLoginService = inject(BiometricLoginService);
  private configMenu = inject(ConfigMenuService)
  private stateService = inject(StateService);

  constructor(protected injector: Injector) {
    this.fb = injector.get(UntypedFormBuilder);
    this.toasterService = injector.get(CustomToasterService);
    this.authService = injector.get(AuthService);
    this.configState = injector.get(ConfigStateService);
    this.route = injector.get(ActivatedRoute);
    this.router = injector.get(Router);
    this.identityLinkUserService = injector.get(IdentityLinkUserService);
    this.recaptchaService = injector.get(RecaptchaService);
    this.securityCodeService = injector.get(SecurityCodeService);
  }

  async ngOnInit() {

    this.init();
    this.buildForm();
    this.setLinkUserParams();

    this.isBiometricAvailable = await this.biometricLoginService.isBiometricAvailable();

    // Tenta login biométrico automaticamente se disponível e credenciais existirem
    if (this.isBiometricAvailable) {
      const creds = await this.biometricLoginService.getRefreshToken();
      if (creds) {
        this.useBiometricLogin = true;
        await this.loginWithBiometrics();
      }
    }
  }

  ngAfterViewInit() {
    this.recaptchaService.setStrategy(
      RECAPTCHA_STRATEGY.Login(this.configState, this.recaptchaRef.nativeElement),
    );
  }

  protected setLinkUserParams() {
    const {
      linkUserId: userId,
      linkToken: token,
      linkTenantId: tenantId,
    } = this.route.snapshot.queryParams;

    if (userId && token) {
      this.identityLinkUserService.verifyLinkToken({ token, userId, tenantId }).subscribe(res => {
        if (res) {
          this.linkUser = { userId, token, tenantId };
        }
      });
    }
  }

  protected init() {
    this.isSelfRegistrationEnabled =
      (
        (this.configState.getSetting('Abp.Account.IsSelfRegistrationEnabled') as string) || ''
      ).toLowerCase() !== 'false';
  }

  protected buildForm() {
    this.form = this.fb.group({
      username: ['', [required, maxLength(255)]],
      password: ['', [required, maxLength(128)]],
      rememberMe: [false],
    });
  }

  onSubmit() {
    if (this.form.invalid) return;

    this.inProgress = true;

    // Zera o estado de navegacao das listas antes de navegar: sem o reload
    // antigo, o StateService (singleton em memoria) manteria a paginacao/filtros
    // da sessao anterior, e a lista de ofertas cairia num filtro stale (ex.:
    // tryAceite=true) que volta vazia ate um F5.
    this.stateService.clear();

    const { username, password, rememberMe } = this.form.value;
    const redirectUrl = getRedirectUrl(this.injector) || (this.linkUser ? null : '/');
    const loginParams = { username, password, rememberMe, redirectUrl };


    // Fluxo stock do ABP: authService.login() -> pipeToLogin, que ja faz
    // refreshAppState() + rememberMeService.set() + router.navigate (SPA). Antes
    // o login chamava loginUsingGrant('password') direto + window.location.href,
    // o que recarregava o app (abortando o application-configuration em curso) e
    // nunca gravava o rememberMe (a sessao expirava no proximo cold-start).
    this.checkRecaptcha()
      .pipe(
        switchMap(isValid =>
          isValid
            ? this.authService.login(loginParams).pipe(
                // Estabelece o cofre biometrico com o RT recem-emitido. A partir
                // dai a rotacao e mantida em sincronia pelo BiometricSessionSyncService.
                tap(() => this.saveRefreshTokenToVault()),
                this.handleLoginError(loginParams),
                this.linkUser ? this.switchToLinkUser() : tap(),
              )
            : of(null)
        ),
        finalize(() => (this.inProgress = false))
      )
      .subscribe();
  }

  private async saveRefreshTokenToVault(): Promise<void> {
    try {
      const refreshToken = this.authService.getRefreshToken();
      if (refreshToken) {
        await this.biometricLoginService.saveRefreshToken(refreshToken);
      }
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  }

  private checkRecaptcha() {
    return this.recaptchaService.isEnabled ? this.recaptchaService.validate() : of(true);
  }

  private switchToLinkUser() {
    return pipe(
      switchMap(() => this.identityLinkUserService.link(this.linkUser)),
      tap(() => {
        this.router.navigate(['/account/link-logged'], {
          queryParams: this.route.snapshot.queryParams,
        });
      }),
    );
  }

  private handleLoginError(loginParams?: LoginParams) {
    return catchError(err => {
      const errorDescription = err.error?.error_description;

      switch (errorDescription) {
        case REQUIRES_TWO_FACTOR:
          const data = {
            ...loginParams,
            userId: err.error.userId,
          };
          this.securityCodeService.data = { ...data, twoFactorToken: err.error.twoFactorToken };
          this.recoveryCodeService.data.set({ ...data });
          return from(this.router.navigate(['/account/send-security-code']));
        case PERIODICALLY_CHANGE_PASSWORD:
        case SHOULD_CHANGE_PASSWORD_ON_NEXT_LOGIN: {
          const queryParams = {
            token: err.error.changePasswordToken,
            redirectUrl: loginParams.redirectUrl,
            username: loginParams.username,
          };
          return from(
            this.router.navigate(['/account/change-password'], {
              queryParams,
            }),
          );
        }
      }

      this.recaptchaService.reset();
      this.toasterService.error(
        err.error?.error_description ||
        err.error?.error?.message ||
        'AbpAccount::DefaultErrorMessage',
        null,
        { life: 7000 },
      );
      return throwError(() => err);
    });
  }

  openPrivacyPolicies() {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/privacy-policy`])
    );

    window.open(url, '_blank');
  }

  async loginWithBiometrics() {
    try {
      this.inProgress = true;
      const authenticated = await this.biometricLoginService.authenticateWithBiometrics();
      if (!authenticated) {
        this.useBiometricLogin = false; // Desmarca o checkbox se o usuário cancelar
        this.inProgress = false;
        return;
      }

      const creds = await this.biometricLoginService.getRefreshToken();
      if (!creds) {
        this.toasterService.warn('Biometria ativada! Por favor, faça login manualmente pelo menos uma vez.', "Autenticação com biometria", {});
        this.inProgress = false;
        return;
      }

      // Mesma limpeza do fluxo por senha: a reentrada biometrica tambem e SPA,
      // entao zera o estado de navegacao para nao herdar filtros/paginacao stale.
      this.stateService.clear();

      await this.authService.loginUsingGrant("refresh_token", {
        refresh_token: creds
      }).then(() => {
        this.configState.refreshAppState().subscribe({
          next: () => {
            this.configMenu.configGlobal()
            this.configMenu.configTenants()
            const refrsh_token = this.authService.getRefreshToken()
            if (refrsh_token) {
              this.biometricLoginService.saveRefreshToken(refrsh_token)
            }
            const redirectUrl = getRedirectUrl(this.injector) || (this.linkUser ? null : '/');
            this.inProgress = false;
            // Navegacao SPA em vez de window.location.href pra nao recarregar o
            // app inteiro (que aborta o application-configuration em curso).
            this.router.navigateByUrl(redirectUrl || '/')
          }
        })

      })

    } catch (error) {
      console.error("Erro ao fazer autenticação: ", JSON.stringify(error))
      if (error.status == 400) {
        this.inProgress = false;
        this.toasterService.warn('Sua sessão expirou. Faça login com as credenciais novamente.', "Autenticação com biometria", {});
      } else {
        this.toasterService.error('Erro ao autenticar com biometria. Tente novamente ou entre com usuário e senha.');
        this.inProgress = false;
        this.useBiometricLogin = false; // Desmarca o checkbox em caso de erro
      }
    }

    this.inProgress = false;
  }

  async onBiometricCheckboxChange() {
    if (this.useBiometricLogin) {
      await this.loginWithBiometrics();
    }
  }
}
